// When you dispatch, state.changes is populated so we wait until that is done before saving.

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import zionDebug from '@fs/zion-debug'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { trackEvent } from '@fs/zion-analytics'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  saveSlsAndUpdateState,
  getNextArkId,
  createFieldValue,
  generateSls,
  useSlsHighlightsContext,
  useSlsStateContext,
  useSlsDispatchContext,
} from '@fs/zion-sls'
import ErrorOverlay from '../../../../../shared/components/errorOverlay/ErrorOverlay'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import { headers } from '../../../../../shared/services/config'
import { errorCollector } from '../../../../../shared/utils/utils'
import AddName from './AddName'
import AddRecordType from './AddRecordType'
import validationSchema from './utils/validation'
import { prepareDispatches } from './utils/dispatchUtils'

const debug = zionDebug('rip:AddPerson')

/**
 * Callback function for when the person id has been fetched
 *
 * @callback onGetPerson
 * @param {String} personId - The generated person id.
 */

/**
 * Callback function for the onSave event data.
 *
 * @callback onSave
 * @param {String} personId - The generated person id.
 * @param {String} recordId - The generated record id.
 * @param {Object[]} dispatches - An array of dispatches.
 * @param {Object[]} formValues - The form values object from the event panel.
 */

/**
 * @component AddPerson Component - renders a form to add a new person to the SLS
 *
 * @param {String} userId - cis user id to use for the save attribution
 * @param {Boolean} useSexField - whether to show the sex field
 * @param {Boolean} noRecord - whether to show the choose record panel (panel 2)
 * @param {Boolean} shouldSaveToSls - if false, the onSave callback will be invoked with the dispatches and form values
 * @param {Function} onSave - callback to invoke with the dispatches or after saving the person
 * @param {Function} onGetPerson - callback to invoke with the generated person id
 * @param {String} splitScreenArk - the ark of the image in the split screen, if any
 * @param {String} appName - We have many apps.  Needs to be the right one who is using this component.  E.g. RII
 * @param {String} recordId - Record id to use when adding the person, this is the record the person will be linked to.
 * The record must already exist in SLS. If passing null or undefined, a new record will be created. This will override the noRecord prop.
 * @returns {Element}
 */
export default function AddPerson({
  userId,
  useSexField = true,
  noRecord = false,
  shouldSaveToSls = true,
  onSave = () => {},
  onGetPerson = () => {},
  splitScreenArk = null,
  appName,
  recordId: parentRecordId = null,
}) {
  if (!appName) throw new Error('AddPanel: appName is required parameter')

  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])
  const { closeOverlay, openOverlay, addPersonGenericOverlay } = useOverlayContext()
  const r2Dispatch = useR2DispatchContext()
  // Set even when onGetPerson is the default no-op, so a render site that skips it would clear a claim
  // it never made. Only IndexImage renders this today, and it wires it up.
  const hasClaimedCreatedPersonId = useRef(false)
  const slsState = useSlsStateContext()
  const slsDispatch = useSlsDispatchContext()
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const [generatedPersonId, setGeneratedPersonId] = useState(null)
  const [saveError, setSaveError] = useState()
  const [saving, setSaving] = useState()
  const step = useRef(1)
  const { getHighlights } = h

  const hasSelectedHighlights = useCallback(
    (fieldId) => {
      return (
        getHighlights().mainEditHighlights.find((highlight) => highlight.fieldId === fieldId) ||
        getHighlights().splitScreenEditHighlights.find((highlight) => highlight.fieldId === fieldId)
      )
    },
    [getHighlights]
  )

  const schema = validationSchema(hasSelectedHighlights)

  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      givenCheckbox: false,
      givenField: '',
      surnameCheckbox: false,
      surnameField: '',
      sexField: '',
      givenNameFieldId: null,
      surnameFieldId: null,
      sexFieldId: null,
      nameType: 'BIRTH_NAME',
      recordType: null,
    },
  })

  function saveSls(sls, dispatches, values, recordId) {
    saveSlsAndUpdateState({
      sls,
      dispatches: dispatches.fullArray,
      saveChangesFlag,
      userId,
      dispatch: slsDispatch,
      appName,
      fsUserAgentChain: headers['FS-User-Agent-Chain'],
      successHandler: () => {
        closeOverlay('addPersonPage1Generic')
        closeOverlay('addPersonPage2Generic')
        if (onSave) {
          onSave({
            personId: generatedPersonId,
            recordId,
            dispatches,
            formValues: values,
          })
        }
      },
      errorHandler: (err) => {
        // eslint-disable-next-line no-warning-comments
        // TODO: Add unable to save changes modal)
        setSaveError(err)
        errorCollector(err, null, {
          codeId: 69,
          sls,
          status: err?.response?.status,
          responseBody: err?.response?.data,
        })
      },
      finalHandler: () => {
        setSaving(false)
      },
    })
  }

  const handleBack = () => {
    step.current = 1
  }

  const handleSubmit = async (values) => {
    const { givenField, surnameField, sexField, givenNameFieldId, surnameFieldId, sexFieldId, nameType } = values
    // If a parent record id is not passed, we need to show the choose record panel (panel 2)
    if (step.current === 1 && !noRecord && !parentRecordId) {
      openOverlay('addPersonPage2Generic')
      trackEvent({
        event_name: 'click_action',
        link_name: `Add: Next`,
      })
      step.current = 2
      setSaving(false)
    } else {
      trackEvent({
        event_name: 'add_person_edit',
        link_name: `Add: Save`,
      })
      setSaving(true)

      // Save information
      const attribution = { comment: '', reasonId: '', app: appName }
      const partitionKey = slsState.currentImageArk

      // Create batch create params
      const batchPersonParams = { partitionKey, givenNameFieldId, surnameFieldId, nameType, sexFieldId }
      if (givenField) batchPersonParams.givenNameFieldValue = createFieldValue(givenField, attribution)
      if (surnameField) batchPersonParams.surnameFieldValue = createFieldValue(surnameField, attribution)
      if (useSexField && sexField) batchPersonParams.sexFieldValue = createFieldValue(sexField.primaryText, attribution)
      batchPersonParams.primaryPerson = false

      // If we don't pass a recordId, one will be created
      const recordId = parentRecordId || (await getNextArkId('record'))

      // Finish batch create params
      batchPersonParams.recordId = recordId
      batchPersonParams.personId = generatedPersonId

      const dispatches = prepareDispatches({
        batchPersonParams,
        images: slsState.images,
        currentImageArk: slsState.currentImageArk,
        recordType: values.recordType,
        types: slsState.types,
        highlights: h.getHighlights(),
        skipRecordCreation: Boolean(parentRecordId),
      })
      if (shouldSaveToSls) {
        const slsActions = generateSls(dispatches.fullArray)
        saveSls(slsActions, dispatches, values, recordId)
      } else if (onSave) {
        onSave({
          personId: generatedPersonId,
          recordId,
          dispatches,
          formValues: values,
        })
        closeOverlay('addPersonPage1Generic')
        closeOverlay('addPersonPage2Generic')
      }
    }
  }

  useEffect(() => {
    let isMounted = true
    getNextArkId('person').then((personId) => {
      if (isMounted) {
        setGeneratedPersonId(personId)
        hasClaimedCreatedPersonId.current = true
        if (onGetPerson) {
          onGetPerson(personId)
        }
      }
    })
    // Runs on unmount, and also on any isOpen flip while mounted, which re-fetches the id above.
    return () => {
      isMounted = false
      if (hasClaimedCreatedPersonId.current) {
        hasClaimedCreatedPersonId.current = false
        r2Dispatch({ type: 'SET_CREATED_PERSON_ID', value: '' })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addPersonGenericOverlay?.isOpen])

  debug('Render: ', { state: slsState })

  return (
    <ZionForm id="add-person-form" methods={methods} onSubmit={handleSubmit}>
      <AddName
        useSexField={useSexField}
        saving={saving}
        hasSelectedHighlights={hasSelectedHighlights}
        splitScreenArk={splitScreenArk}
      />
      <AddRecordType
        saving={saving}
        selectedPersonId={generatedPersonId}
        handleBack={handleBack}
        submitFunc={handleSubmit}
      />
      <ErrorOverlay
        error={saveError}
        setError={setSaveError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </ZionForm>
  )
}
